// #12 — Public findings dashboard. Renders /public/findings (no auth)
// for the opt-in aggregate findings. Layout matches the spec example:
// "Across 412 TypeScript UI-polish receipts, Team Coral-Lite using
// Gemini visual review plus Qwen implementation delivered 71% lower
// cost than Claude-only teams, with 8% more human corrections and
// similar final approval."

import { useEffect, useState } from "react";
import { ApiError, listPublicFindings, type PublicFindingRow } from "../api/client";

export function PublicFindingsPage() {
  const [findings, setFindings] = useState<PublicFindingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPublicFindings()
      .then((rows) => {
        if (!cancelled) setFindings(rows);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto flex min-h-screen max-w-4xl flex-col gap-6 px-6 py-12">
      <header>
        <h1 className="text-lg font-semibold text-primary">
          Waytide — public findings
        </h1>
        <p className="mt-1 text-xs text-muted">
          Receipt-backed aggregate claims from opt-in Workspaces. Every
          finding cites a specific task class, a real receipt count, and
          a quantified outcome. No vendor benchmarks, no marketing
          numbers — just what the field shipped.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger">
          Could not load findings: {error}
        </div>
      )}

      {!findings && !error && (
        <p className="text-xs text-muted">Loading…</p>
      )}

      {findings && findings.length === 0 && (
        <div className="rounded-md border border-edge bg-surface-raised px-4 py-6 text-center">
          <p className="text-sm text-primary">No public findings yet.</p>
          <p className="mt-2 text-xs text-muted">
            Findings appear here as Waytide aggregates real Workspace
            receipts that have been explicitly opted-in for public
            sharing. Volume gates publication; nothing is published
            until the data is non-embarrassingly useful.
          </p>
        </div>
      )}

      {findings && findings.length > 0 && (
        <ul className="flex flex-col gap-3">
          {findings.map((f, idx) => {
            let tags: string[] = [];
            try {
              tags = JSON.parse(f.task_tags_json);
            } catch {
              /* ignore */
            }
            return (
              <li
                key={`${f.created_at}-${idx}`}
                className="rounded-md border-l-2 border-accent bg-surface-raised px-4 py-3"
                data-testid={`public-finding-${idx}`}
              >
                <p className="text-sm text-primary">{f.claim}</p>
                <p className="mt-2 text-[0.625rem] text-muted">
                  n = {f.aggregate_count} · confidence{" "}
                  {Math.round(f.confidence * 100)}%
                  {tags.length > 0 && (
                    <span className="ml-2">
                      {tags.map((t) => (
                        <span
                          key={t}
                          className="ml-1 rounded bg-surface px-1 py-0.5 font-code text-[0.625rem] text-faint"
                        >
                          {t}
                        </span>
                      ))}
                    </span>
                  )}
                  <span className="ml-2 font-code text-[0.625rem] text-faint">
                    {new Date(f.created_at / 1000).toLocaleDateString()}
                  </span>
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
